import { DatePipe } from "@angular/common";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
    ApplicationConfig,
    ErrorHandler,
    inject,
    isDevMode,
    LOCALE_ID,
    provideAppInitializer,
    provideZoneChangeDetection
} from "@angular/core";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import { getAuth, provideAuth } from "@angular/fire/auth";
import { ValidationErrors } from "@angular/forms";
import { provideDateFnsAdapter } from "@angular/material-date-fns-adapter";
import { MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS, MatButtonToggleDefaultOptions } from "@angular/material/button-toggle";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { MAT_DIALOG_DEFAULT_OPTIONS } from "@angular/material/dialog";
import { MAT_TABS_CONFIG, MatTabsConfig } from "@angular/material/tabs";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, withComponentInputBinding, withViewTransitions } from "@angular/router";
import { provideTransloco, TranslocoService } from "@jsverse/transloco";
import { provideFormFieldError } from "@recursyve/ngx-material-components/form-field-error";
import { provideNiceFilterViewTranslater } from "@recursyve/nice-filter-view/common";
import { createErrorHandler } from "@sentry/angular";
import { format, Locale } from "date-fns";
import { enCA, es, frCA } from "date-fns/locale";
import { provideNgxMask } from "ngx-mask";
import { provideToastr } from "ngx-toastr";
import { LanguagePreloadService } from "../common/i18n/language-preloader.service";
import { TranslocoHttpLoader } from "../common/i18n/transloco/transloco-loader";
import { environment } from "../environments/environment";
import { provideApi } from "./api/api.provider";
import { AuthInterceptor } from "./api/interceptors/auth.interceptor";
import { LanguageInterceptor } from "./api/interceptors/language.interceptor";
import { TimeZoneInterceptor } from "./api/interceptors/time-zone.interceptor";
import { routes } from "./app.routes";

const languageMap: Record<string, Locale> = {
    fr: frCA,
    en: enCA,
    es: es
};

export const appConfig: ApplicationConfig = {
    providers: [
        DatePipe,
        provideZoneChangeDetection({ eventCoalescing: true }),
        provideRouter(routes, withViewTransitions(), withComponentInputBinding()),
        provideAnimations(),
        provideToastr(),
        provideFirebaseApp(() => initializeApp(environment.firebase)),
        provideAuth(() => getAuth()),
        provideHttpClient(withInterceptors([AuthInterceptor, LanguageInterceptor, TimeZoneInterceptor])),
        provideApi({ rootUrl: environment.apiUrl }),
        provideDateFnsAdapter(),
        provideNgxMask(),
        provideTransloco({
            config: {
                availableLangs: ["fr", "en", "es"],
                defaultLang: "fr",
                reRenderOnLangChange: false,
                prodMode: !isDevMode()
            },
            loader: TranslocoHttpLoader
        }),

        provideFormFieldError({
            translater: {
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params),
                deps: [TranslocoService]
            },
            errorTransformers: {
                matDatepickerMin: (error: string, details: ValidationErrors) => {
                    return {
                        key: error,
                        params: {
                            min: format(details["min"], "yy-MM-dd"),
                            actual: format(details["actual"], "yy-MM-dd")
                        }
                    };
                }
            }
        }),

        provideNiceFilterViewTranslater({
            translater: {
                useFactory: (service: TranslocoService) => (key, params) => service.translate(key, params),
                deps: [TranslocoService]
            }
        }),

        provideAppInitializer(() => {
            const service = inject(LanguagePreloadService);
            return service.preload();
        }),
        { provide: LOCALE_ID, useFactory: () => languageMap["fr"].code },
        {
            provide: MAT_DATE_LOCALE,
            useFactory: () => languageMap["fr"]
        },
        {
            provide: ErrorHandler,
            useValue: createErrorHandler()
        },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: {
                restoreFocus: false,
                autoFocus: ".first-focus",
                exitAnimationDuration: 100
            }
        },
        {
            provide: MAT_BUTTON_TOGGLE_DEFAULT_OPTIONS,
            useValue: {
                hideMultipleSelectionIndicator: true,
                hideSingleSelectionIndicator: true
            } satisfies MatButtonToggleDefaultOptions
        },
        {
            provide: MAT_TABS_CONFIG,
            useValue: {
                stretchTabs: false,
                disablePagination: true
            } satisfies MatTabsConfig
        }
    ]
};
